import { useState } from 'react';
import type { MoneyMonth, PersonalBill, PersonalIncome } from '../../../lib/api/team.js';
import { moneyCsv } from '../../../lib/moneyCsv.js';
import { hapticTap, saveBlob } from '../../../lib/native.js';
import { PageShell } from '../../stage/PageShell.js';
import { money } from './Ganhos.js';

/**
 * Os últimos meses do Dinheiro: entrou e saiu lado a lado, o quanto sobrou e a
 * taxa de sobra (do que entrou, quanto ficou). O mês atual mostra a sobra
 * prevista; os fechados mostram o que de fato sobrou. A planilha leva tudo.
 */

function monthName(month: string, style: 'long' | 'short' = 'long'): string {
  const [year, index] = month.split('-').map(Number) as [number, number];
  const name = new Intl.DateTimeFormat('pt-BR', { month: style, timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, index - 1, 15)))
    .replace('.', '');
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** O que sobrou: no mês atual, o previsto; nos fechados, o que entrou menos o que foi pago. */
const leftover = (item: MoneyMonth, current: boolean) =>
  current ? item.leftoverCents : item.receivedCents - item.paidCents;

const hasMoves = (item: MoneyMonth) =>
  item.receivedCents + item.expectedCents + item.paidCents + item.dueCents > 0;

export function DinheiroMeses({
  history,
  bills,
  incomes,
  onBack,
}: {
  history: MoneyMonth[];
  bills: PersonalBill[];
  incomes: PersonalIncome[];
  onBack: () => void;
}) {
  const current = history.at(-1)?.month ?? '';
  const [picked, setPicked] = useState(current);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState('');
  const active = history.filter(hasMoves);
  const closed = active.filter((item) => item.month !== current);
  const average = closed.length
    ? Math.round(closed.reduce((sum, item) => sum + leftover(item, false), 0) / closed.length)
    : null;
  const rates = closed.filter((item) => item.savingsRate !== null);
  const averageRate = rates.length
    ? Math.round(rates.reduce((sum, item) => sum + (item.savingsRate ?? 0), 0) / rates.length)
    : null;
  const top = Math.max(1, ...history.map((item) => Math.max(item.receivedCents, item.paidCents)));
  const chosen = history.find((item) => item.month === picked) ?? history.at(-1);
  // Meses vazios antes da primeira anotação não contam história nenhuma.
  const first = history.findIndex(hasMoves);
  const shown = first < 0 ? history.slice(-1) : history.slice(first);

  async function exportCsv() {
    setSaving(true);
    setFailed('');
    try {
      void hapticTap();
      const months = history.map((item) => item.month);
      const csv = moneyCsv(bills, incomes, months);
      const name = `dinheiro-${months[0]}-a-${months.at(-1)}.csv`;
      await saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), name);
    } catch {
      setFailed('Não consegui preparar a planilha agora. Tente de novo em instantes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell
      kicker="Dinheiro"
      title="Seus últimos meses"
      lead="O que entrou, o que saiu e quanto ficou, mês a mês."
      onBack={onBack}
    >
      {average !== null ? (
        <div className="pgs-figure">
          <b>{money(average)}</b>
          <small>
            de sobra média em{' '}
            {closed.length === 1 ? '1 mês fechado' : `${closed.length} meses fechados`}
            {averageRate !== null && `, ${averageRate}% do que entrou`}
          </small>
        </div>
      ) : (
        <div className="pgs-note">
          <i>“</i>
          <span>
            <b>A média aparece quando o primeiro mês fechar</b>
            <small>Até lá, eu mostro a sobra prevista deste mês.</small>
          </span>
        </div>
      )}

      <section className="pgs-section" aria-label="Mês a mês">
        {shown.map((item) => {
          const now = item.month === current;
          const left = leftover(item, now);
          const empty = !hasMoves(item);
          return (
            <button
              key={item.month}
              type="button"
              className={`dm-month${picked === item.month ? ' on' : ''}${empty ? ' quiet' : ''}`}
              aria-pressed={picked === item.month}
              onClick={() => setPicked(item.month)}
            >
              <span className="dm-head">
                <b>
                  {monthName(item.month)}
                  {now && <em>agora</em>}
                </b>
                <span className={`dm-left${left < 0 ? ' short' : ''}`}>
                  {empty
                    ? 'sem anotações'
                    : `${left < 0 ? 'faltou' : now ? 'deve sobrar' : 'sobrou'} ${money(Math.abs(left))}`}
                </span>
              </span>
              {!empty && (
                <span className="dm-bars" aria-hidden="true">
                  <i className="in" style={{ width: `${(item.receivedCents / top) * 100}%` }} />
                  <i className="out" style={{ width: `${(item.paidCents / top) * 100}%` }} />
                </span>
              )}
              {!empty && (
                <small className="dm-line">
                  Entrou {money(item.receivedCents)} · saiu {money(item.paidCents)}
                  {!now &&
                    item.savingsRate !== null &&
                    ` · ficou ${Math.max(item.savingsRate, 0)}%`}
                </small>
              )}
            </button>
          );
        })}
      </section>

      {chosen && (chosen.byCategory.length > 0 || chosen.byLabel.length > 0) && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            De onde veio <small>{monthName(chosen.month)}</small>
          </h2>
          {chosen.byCategory.map((group) => (
            <div className="pgs-bar dm-bar" key={group.category}>
              <span>{group.label}</span>
              <b>{money(group.cents)}</b>
              <i>
                <em
                  style={{
                    width: `${Math.max(4, Math.round((group.cents / chosen.receivedCents) * 100))}%`,
                  }}
                />
              </i>
            </div>
          ))}
          {chosen.byLabel.length > 0 && (
            <div className="pgs-chips dm-labels" aria-label="Seus rótulos">
              {chosen.byLabel.map((group) => (
                <span className="pgs-chip dm-chip" key={group.label}>
                  {group.label} · {money(group.cents)}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {failed && (
        <p className="cnt-error" role="alert">
          {failed}
        </p>
      )}
      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn solid"
          disabled={saving || active.length === 0}
          onClick={() => void exportCsv()}
        >
          {saving ? 'Preparando…' : 'Baixar a planilha'}
        </button>
      </div>
      <p className="cnt-trust">
        A planilha abre no Excel e no Planilhas Google, com os ganhos e as contas destes meses.
      </p>
    </PageShell>
  );
}
